import { useRef, useState } from "react"
import { Box, InputBase, Popover, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { MARKETING_TAG_OPTIONS } from "../../weekMenuLib"
import { PlannerTag, PlannerTagVariant } from "../PlannerTag"
import { cellFieldMissingStyle, cellFieldStyle } from "./cellFieldStyles"

const useStyles = makeStyles()((theme) => ({
    input: {
        ...cellFieldStyle(theme),
        width: "100%",
        padding: ".25rem",
        color: theme.palette.tailwind.slate[800],
        fontWeight: 600,
        fontSize: ".75rem",
        "& input::placeholder": {
            color: theme.palette.tailwind.slate[500],
            fontWeight: 400,
            opacity: 1,
        },
    },
    inputError: cellFieldMissingStyle(theme),
    // The tag itself hugs its text, so the wrapper is what the popover anchors to.
    tagWrap: {
        alignSelf: "flex-start",
    },
    popover: {
        padding: ".5rem",
        maxWidth: "15rem",
    },
    popoverTitle: {
        color: theme.palette.tailwind.gray[500],
        textTransform: "uppercase",
        fontWeight: 700,
    },
    addCustomTag: {
        display: "flex",
        alignItems: "center",
        gap: ".375rem",
        padding: 0,
        border: "none",
        background: "transparent",
        color: theme.palette.tailwind.blue[500],
        fontWeight: 600,
        fontSize: ".75rem",
        cursor: "pointer",
        "& svg": {
            fontSize: ".625rem",
        },
    },
}))

type Props = Readonly<{
    value: string
    hasError: boolean
    onChange: (value: string) => void
}>

export function CellMarketingField({ value, hasError, onChange }: Props) {
    const { classes, cx } = useStyles()

    const anchorRef = useRef<HTMLDivElement | null>(null)
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [isEditing, setEditing] = useState(false)

    const isKnownTag = MARKETING_TAG_OPTIONS.includes(value)

    const pickTag = (tag: string) => {
        onChange(tag)
        setShowSuggestions(false)
        setEditing(false)
    }

    if (isKnownTag && !isEditing) {
        return (
            <Stack ref={anchorRef} className={classes.tagWrap}>
                {/* Only switch to editing, so the popover anchors to the mounted input and not the unmounted chip. */}
                <PlannerTag variant={PlannerTagVariant.MARKETING} onClick={() => setEditing(true)}>
                    {value}
                </PlannerTag>
            </Stack>
        )
    }

    return (
        <>
            <LocalizedStrict id="merchant-week-menu-cell-marketing-field" attrs={{ placeholder: true }}>
                <InputBase
                    ref={anchorRef}
                    value={value}
                    autoFocus={isEditing}
                    placeholder="Marketing tag (optional)"
                    className={cx(classes.input, hasError && classes.inputError)}
                    onChange={(event) => onChange(event.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => {
                        setShowSuggestions(false)
                        setEditing(false)
                    }}
                />
            </LocalizedStrict>
            <Popover
                open={showSuggestions}
                anchorEl={anchorRef.current}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                disableAutoFocus
                disableEnforceFocus
                onClose={() => setShowSuggestions(false)}
            >
                <Box className={classes.popover}>
                    <Stack spacing=".375rem">
                        <LocalizedStrict id="merchant-week-menu-marketing-popover-title">
                            <Typography variant="caption" component="div" className={classes.popoverTitle}>
                                Suggested tags
                            </Typography>
                        </LocalizedStrict>
                        <Stack direction="row" flexWrap="wrap" gap=".25rem">
                            {MARKETING_TAG_OPTIONS.map((tag) => (
                                <Stack
                                    key={tag}
                                    className={classes.tagWrap}
                                    onMouseDown={(event) => {
                                        event.preventDefault()
                                        pickTag(tag)
                                    }}
                                >
                                    <PlannerTag variant={PlannerTagVariant.MARKETING} onClick={() => pickTag(tag)}>
                                        {tag}
                                    </PlannerTag>
                                </Stack>
                            ))}
                        </Stack>
                        <button
                            type="button"
                            className={classes.addCustomTag}
                            // Keeps the input focused so the user can type their own tag right away.
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => setShowSuggestions(false)}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <LocalizedStrict id="merchant-week-menu-marketing-add-custom-tag">
                                <span>Add custom tag</span>
                            </LocalizedStrict>
                        </button>
                    </Stack>
                </Box>
            </Popover>
        </>
    )
}
